import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { withdrawalAPI } from '../api/client';

export const WithdrawPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [paymentMethod, setPaymentMethod] = useState('bank');
  const [formData, setFormData] = useState({
    amount: '',
    // Bank fields
    accountNumber: '',
    ifsc: '',
    accountHolderName: '',
    bankName: '',
    // USDT fields
    usdtAddress: '',
    usdtNetwork: 'ERC20',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateUSDTAddress = (address, network) => {
    if (network === 'ERC20') {
      // Ethereum: 42 chars (0x + 40 hex)
      return /^0x[a-fA-F0-9]{40}$/.test(address);
    } else if (network === 'TRC20') {
      // Tron: 34 chars, starts with T
      return /^T[a-zA-Z0-9]{33}$/.test(address);
    }
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const requestData = {
        amount: parseFloat(formData.amount),
        paymentMethod: paymentMethod,
      };

      if (paymentMethod === 'bank') {
        if (!formData.accountNumber || !formData.ifsc || !formData.accountHolderName || !formData.bankName) {
          setError('All bank details are required');
          setLoading(false);
          return;
        }
        requestData.accountNumber = formData.accountNumber;
        requestData.ifsc = formData.ifsc;
        requestData.accountHolderName = formData.accountHolderName;
        requestData.bankName = formData.bankName;
      } else if (paymentMethod === 'usdt') {
        if (!formData.usdtAddress || !formData.usdtNetwork) {
          setError('USDT address and network are required');
          setLoading(false);
          return;
        }
        if (!validateUSDTAddress(formData.usdtAddress, formData.usdtNetwork)) {
          setError(`Invalid ${formData.usdtNetwork} address format`);
          setLoading(false);
          return;
        }
        requestData.usdtAddress = formData.usdtAddress;
        requestData.usdtNetwork = formData.usdtNetwork;
      }

      const response = await withdrawalAPI.request(requestData);
      setSuccess(true);
      setTimeout(() => navigate('/withdrawals'), 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Error processing withdrawal');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center">
        <div className="bg-slate-800 rounded-xl shadow-lg p-8 text-center border border-slate-700">
          <div className="text-5xl mb-4">✅</div>
          <h2 className="text-2xl font-bold text-emerald-400 mb-2">Withdrawal Requested!</h2>
          <p className="text-gray-400">Your withdrawal is being processed. You will be redirected shortly.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800">
      <nav className="bg-slate-800 border-b border-slate-700 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded flex items-center justify-center text-lg font-bold">₹</div>
            <h1 className="text-2xl font-bold text-white">PaySolutions</h1>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            className="text-gray-400 hover:text-yellow-400 transition"
          >
            Back
          </button>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto py-12 px-4">
        <div className="bg-slate-800 rounded-xl shadow-lg p-8 border border-slate-700">
          <h2 className="text-2xl font-bold text-white mb-6">Withdraw Money</h2>

          {error && (
            <div className="bg-red-900/20 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg mb-4 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Amount (INR)</label>
              <input
                type="number"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                required
                min="1"
                step="0.01"
                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/20 text-white placeholder-gray-500"
                placeholder="1000.00"
              />
              {formData.amount && (
                <p className="text-xs text-gray-400 mt-2">
                  You'll receive: <span className="text-emerald-400 font-semibold">₹{(parseFloat(formData.amount) * 0.9).toFixed(2)}</span> (10% commission)
                </p>
              )}
            </div>

            {/* Payment Method Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">Withdrawal Method</label>
              <div className="space-y-3">
                <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer transition" style={{borderColor: paymentMethod === 'bank' ? '#60a5fa' : 'rgb(71 85 105)', backgroundColor: paymentMethod === 'bank' ? 'rgb(30 58 138 / 0.1)' : 'transparent'}}>
                  <input
                    type="radio"
                    value="bank"
                    checked={paymentMethod === 'bank'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="ml-3">
                    <strong className="text-white">Bank Transfer</strong>
                    <p className="text-xs text-gray-400 mt-1">Direct to your bank account (1-2 hours)</p>
                  </span>
                </label>

                <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer transition" style={{borderColor: paymentMethod === 'usdt' ? '#60a5fa' : 'rgb(71 85 105)', backgroundColor: paymentMethod === 'usdt' ? 'rgb(30 58 138 / 0.1)' : 'transparent'}}>
                  <input
                    type="radio"
                    value="usdt"
                    checked={paymentMethod === 'usdt'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="ml-3">
                    <strong className="text-white">USDT (Crypto)</strong>
                    <p className="text-xs text-gray-400 mt-1">To crypto wallet (10-30 minutes)</p>
                  </span>
                </label>
              </div>
            </div>

            {/* Bank Details Section */}
            {paymentMethod === 'bank' && (
              <div className="space-y-4 p-4 bg-blue-600/10 rounded-lg border border-blue-500/30">
                <h3 className="font-semibold text-white">Bank Account Details</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Account Number</label>
                  <input
                    type="text"
                    name="accountNumber"
                    value={formData.accountNumber}
                    onChange={handleChange}
                    required={paymentMethod === 'bank'}
                    className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/20 text-white placeholder-gray-500"
                    placeholder="1234567890"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">IFSC Code</label>
                  <input
                    type="text"
                    name="ifsc"
                    value={formData.ifsc}
                    onChange={handleChange}
                    required={paymentMethod === 'bank'}
                    className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/20 text-white placeholder-gray-500"
                    placeholder="HDFC0001234"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Account Holder Name</label>
                  <input
                    type="text"
                    name="accountHolderName"
                    value={formData.accountHolderName}
                    onChange={handleChange}
                    required={paymentMethod === 'bank'}
                    className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/20 text-white placeholder-gray-500"
                    placeholder="Your Name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Bank Name</label>
                  <input
                    type="text"
                    name="bankName"
                    value={formData.bankName}
                    onChange={handleChange}
                    required={paymentMethod === 'bank'}
                    className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/20 text-white placeholder-gray-500"
                    placeholder="HDFC Bank"
                  />
                </div>
              </div>
            )}

            {/* USDT Details Section */}
            {paymentMethod === 'usdt' && (
              <div className="space-y-4 p-4 bg-purple-600/10 rounded-lg border border-purple-500/30">
                <h3 className="font-semibold text-white">USDT Wallet Details</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Network</label>
                  <select
                    name="usdtNetwork"
                    value={formData.usdtNetwork}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/20 text-white"
                  >
                    <option value="ERC20" className="bg-slate-800">ERC20 (Ethereum)</option>
                    <option value="TRC20" className="bg-slate-800">TRC20 (Tron)</option>
                  </select>
                  <p className="text-xs text-gray-400 mt-2">
                    {formData.usdtNetwork === 'ERC20' 
                      ? 'Ethereum network - Address must be 42 characters starting with 0x'
                      : 'Tron network - Address must be 34 characters starting with T'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">USDT Wallet Address</label>
                  <input
                    type="text"
                    name="usdtAddress"
                    value={formData.usdtAddress}
                    onChange={handleChange}
                    required={paymentMethod === 'usdt'}
                    className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/20 text-white placeholder-gray-500 font-mono text-sm"
                    placeholder={formData.usdtNetwork === 'ERC20' 
                      ? '0x...' 
                      : 'T...'}
                  />
                  <p className="text-xs text-gray-400 mt-2">Please double-check the address - transactions cannot be reversed</p>
                </div>

                <div className="bg-yellow-600/10 border border-yellow-500/30 rounded p-3">
                  <p className="text-xs text-yellow-300">
                    <strong>⚠️ Important:</strong> Make sure this is a {formData.usdtNetwork === 'ERC20' ? 'Ethereum (ERC20)' : 'Tron (TRC20)'} address. Sending to wrong network will result in permanent loss of funds.
                  </p>
                </div>
              </div>
            )}

            {/* Commission Info */}
            <div className="bg-blue-600/10 border border-blue-500/30 rounded-lg p-4">
              <p className="text-sm text-gray-300">
                <strong className="text-white">Processing Time:</strong> {paymentMethod === 'usdt' ? '10-30 minutes' : '1-2 hours'}
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full font-bold py-3 px-4 rounded-lg transition disabled:opacity-50 ${
                paymentMethod === 'bank' 
                  ? 'bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white'
                  : 'bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white'
              }`}
            >
              {loading ? 'Processing...' : `Request ${paymentMethod === 'usdt' ? 'USDT' : 'Withdrawal'}`}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default WithdrawPage;
